import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { useColorTheme } from '../../context/ColorThemeContext';
import { MessageCircle, Wifi, WifiOff, AlertTriangle, QrCode, RefreshCw, LogOut, Send, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WhatsAppSettingsV2 = ({ onBackToMain, sessionToken }) => {
  const { colorTheme } = useColorTheme();
  const [qrCode, setQrCode] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('checking');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [serviceAvailable, setServiceAvailable] = useState(true);

  const gradient = colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-emerald-600 to-teal-500';
  const gradientBg = colorTheme === 'purple' ? 'from-purple-50/80 via-white to-pink-50/50' : colorTheme === 'blue' ? 'from-blue-50/80 via-white to-sky-50/50' : 'from-emerald-50/80 via-white to-teal-50/50';
  const accentText = colorTheme === 'purple' ? 'text-purple-600 dark:text-purple-400' : colorTheme === 'blue' ? 'text-blue-600 dark:text-blue-400' : 'text-emerald-600 dark:text-emerald-400';
  const btnGradient = `bg-gradient-to-r ${gradient}`;

  useEffect(() => { checkStatus(); }, []);

  const checkStatus = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/status`, { headers: { Authorization: `Bearer ${sessionToken}` } });
      if (response.data.status === 'service_unavailable') {
        setServiceAvailable(false);
        setConnectionStatus('service_unavailable');
        return;
      }
      setServiceAvailable(true);
      setConnectionStatus(response.data.status);
      if (response.data.connected) setMessage('WhatsApp is connected!');
    } catch (err) {
      if (err.code === 'ERR_NETWORK' || err.response?.status === 500) {
        setServiceAvailable(false);
        setConnectionStatus('service_unavailable');
      }
    }
  };

  const handleInitialize = async () => {
    setIsLoading(true); setError(''); setMessage(''); setQrCode(null);
    try {
      const response = await axios.post(`${API}/whatsapp/initialize`, {}, { headers: { Authorization: `Bearer ${sessionToken}` } });
      if (response.data.status === 'service_unavailable') {
        setServiceAvailable(false); setConnectionStatus('service_unavailable');
        setError(response.data.message || 'WhatsApp service is not available');
        return;
      }
      if (response.data.status === 'connected') {
        setConnectionStatus('connected'); setMessage('Already connected!');
      } else if (response.data.status === 'qr_ready') {
        setConnectionStatus('qr_ready'); await fetchQRCode();
      } else if (response.data.status === 'timeout') {
        setError(response.data.message || 'Connection timed out.');
      } else if (response.data.status === 'error' || response.data.success === false) {
        setError(response.data.message || 'Failed to initialize WhatsApp. Please try again.');
      }
    } catch (err) {
      if (err.response?.data?.status === 'service_unavailable') {
        setServiceAvailable(false); setConnectionStatus('service_unavailable');
        setError(err.response.data.message);
      } else { setError('Failed to initialize WhatsApp connection.'); }
    } finally { setIsLoading(false); }
  };

  const fetchQRCode = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/qr-code`, { headers: { Authorization: `Bearer ${sessionToken}` } });
      if (response.data.status === 'service_unavailable') { setServiceAvailable(false); return; }
      if (response.data.qr) {
        setQrCode(response.data.qr);
        setMessage('Scan the QR code with your WhatsApp mobile app');
        pollConnection();
      }
    } catch (err) { setError('Failed to fetch QR code'); }
  };

  const pollConnection = () => {
    const interval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/whatsapp/status`, { headers: { Authorization: `Bearer ${sessionToken}` } });
        if (response.data.connected) {
          setConnectionStatus('connected'); setMessage('WhatsApp connected successfully!'); setQrCode(null); clearInterval(interval);
        }
      } catch (e) { clearInterval(interval); }
    }, 2000);
    setTimeout(() => clearInterval(interval), 120000);
  };

  const handleLogout = async () => {
    if (!window.confirm('Are you sure you want to disconnect WhatsApp?')) return;
    setIsLoading(true);
    try {
      await axios.post(`${API}/whatsapp/logout`, {}, { headers: { Authorization: `Bearer ${sessionToken}` } });
      setConnectionStatus('disconnected'); setQrCode(null); setMessage('Disconnected successfully');
    } catch (e) { setError('Failed to disconnect'); }
    finally { setIsLoading(false); }
  };

  const statusConfig = {
    connected: { icon: Wifi, label: 'Connected', dot: 'bg-emerald-500', bg: 'bg-emerald-50 dark:bg-emerald-900/20' },
    qr_ready: { icon: QrCode, label: 'Waiting for scan', dot: 'bg-amber-500 animate-pulse', bg: 'bg-amber-50 dark:bg-amber-900/20' },
    checking: { icon: RefreshCw, label: 'Checking...', dot: 'bg-blue-500 animate-pulse', bg: 'bg-blue-50 dark:bg-blue-900/20' },
    disconnected: { icon: WifiOff, label: 'Disconnected', dot: 'bg-slate-400', bg: 'bg-slate-50 dark:bg-slate-800/50' },
  };

  const status = statusConfig[connectionStatus] || statusConfig.disconnected;
  const StatusIcon = status.icon;

  // Service unavailable
  if (!serviceAvailable || connectionStatus === 'service_unavailable') {
    return (
      <div className={`min-h-screen bg-gradient-to-br ${gradientBg} dark:from-slate-900 dark:via-slate-900 dark:to-slate-900`} data-testid="whatsapp-page">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <h1 className={`text-3xl font-bold bg-gradient-to-r ${gradient} bg-clip-text text-transparent`}>WhatsApp Integration</h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">Connect your personal WhatsApp to publish campaigns directly</p>
          </div>

          <div className="bg-amber-50/80 dark:bg-amber-900/10 backdrop-blur-xl border border-amber-200/50 dark:border-amber-800/50 rounded-2xl p-5 mb-6 shadow-sm">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-bold text-amber-800 dark:text-amber-200 mb-2">Service Unavailable</h3>
                <p className="text-xs text-amber-700 dark:text-amber-300 mb-3">The WhatsApp integration service is currently not available. Possible reasons:</p>
                <ul className="list-disc list-inside text-xs text-amber-600 dark:text-amber-400 space-y-1 mb-4">
                  <li>Service not deployed in production</li>
                  <li>Temporarily offline</li>
                  <li>Network connectivity issues</li>
                </ul>
                <button onClick={checkStatus} className={`px-4 py-2 ${btnGradient} text-white text-xs font-semibold rounded-xl shadow-sm transition-all hover:shadow-md`} data-testid="whatsapp-retry-btn">
                  <span className="flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5" /> Retry Connection</span>
                </button>
              </div>
            </div>
          </div>

          <div className="bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 rounded-2xl p-5 shadow-sm">
            <h3 className="text-sm font-bold text-slate-800 dark:text-white mb-3">Alternative Options</h3>
            <ul className="space-y-2 text-xs text-slate-600 dark:text-slate-300">
              <li className="flex items-center gap-2"><Send className="w-3.5 h-3.5 text-blue-500" /> Publish campaigns via <strong>Telegram</strong></li>
              <li className="flex items-center gap-2"><Send className="w-3.5 h-3.5 text-slate-400" /> Export and share content manually</li>
              <li className="flex items-center gap-2"><MessageCircle className="w-3.5 h-3.5 text-slate-400" /> Contact support for urgent needs</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br ${gradientBg} dark:from-slate-900 dark:via-slate-900 dark:to-slate-900`} data-testid="whatsapp-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className={`text-3xl font-bold bg-gradient-to-r ${gradient} bg-clip-text text-transparent`}>WhatsApp Integration</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">Connect your personal WhatsApp to publish campaigns directly</p>
        </div>

        {/* Connection Status Card */}
        <div className="bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 rounded-2xl p-5 shadow-sm mb-6" data-testid="whatsapp-status-card">
          <h3 className="text-sm font-bold text-slate-800 dark:text-white mb-4">Connection Status</h3>
          <div className="flex items-center justify-between">
            <div className={`flex items-center gap-3 px-4 py-2.5 rounded-xl ${status.bg}`}>
              <span className={`w-2.5 h-2.5 rounded-full ${status.dot}`} />
              <StatusIcon className={`w-4 h-4 ${accentText}`} />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{status.label}</span>
            </div>
            {connectionStatus === 'connected' ? (
              <button onClick={handleLogout} disabled={isLoading}
                className="px-4 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-xs font-semibold rounded-xl hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                data-testid="whatsapp-disconnect-btn">
                <LogOut className="w-3.5 h-3.5" /> {isLoading ? 'Disconnecting...' : 'Disconnect'}
              </button>
            ) : (
              <button onClick={handleInitialize} disabled={isLoading || connectionStatus === 'checking'}
                className={`px-4 py-2 ${btnGradient} text-white text-xs font-semibold rounded-xl shadow-sm hover:shadow-md transition-all disabled:opacity-50 flex items-center gap-1.5`}
                data-testid="whatsapp-connect-btn">
                <Wifi className="w-3.5 h-3.5" /> {isLoading ? 'Connecting...' : 'Connect WhatsApp'}
              </button>
            )}
          </div>

          {message && (
            <div className="mt-4 p-3 bg-emerald-50/80 dark:bg-emerald-900/20 border border-emerald-200/50 dark:border-emerald-800/50 rounded-xl flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
              <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">{message}</span>
            </div>
          )}
          {error && (
            <div className="mt-4 p-3 bg-red-50/80 dark:bg-red-900/20 border border-red-200/50 dark:border-red-800/50 rounded-xl flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0" />
              <span className="text-xs font-medium text-red-700 dark:text-red-300">{error}</span>
            </div>
          )}
        </div>

        {/* QR Code */}
        {qrCode && (
          <div className="bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 rounded-2xl p-6 shadow-sm mb-6 text-center" data-testid="whatsapp-qr-section">
            <QrCode className={`w-6 h-6 mx-auto mb-3 ${accentText}`} />
            <h3 className="text-sm font-bold text-slate-800 dark:text-white mb-4">Scan QR Code with WhatsApp</h3>
            <div className="inline-block p-4 bg-white rounded-2xl shadow-inner">
              <QRCodeSVG value={qrCode} size={220} level="H" />
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-4">Open WhatsApp on your phone → Settings → Linked Devices → Link a Device</p>
          </div>
        )}

        {/* How to Use */}
        <div className="bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 rounded-2xl p-5 shadow-sm" data-testid="whatsapp-instructions">
          <h3 className="text-sm font-bold text-slate-800 dark:text-white mb-4">How to Use</h3>
          <div className="space-y-3">
            {[
              { step: '1', text: 'Click "Connect WhatsApp" above' },
              { step: '2', text: 'Scan the QR code with your WhatsApp mobile app' },
              { step: '3', text: 'Once connected, publish campaigns from the campaign details page' },
              { step: '4', text: 'Send to multiple numbers or WhatsApp groups' },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3">
                <span className={`w-6 h-6 rounded-lg ${btnGradient} text-white text-xs font-bold flex items-center justify-center flex-shrink-0`}>{item.step}</span>
                <span className="text-sm text-slate-600 dark:text-slate-300">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppSettingsV2;
